import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MsxIcons from "../../Common/components/Icons/MsxIcons/MsxIcons";
import Input from "../../Common/components/Inputs/Input/Input";
import { STEPS } from "../../Common/store/data/commonData";
import { updateConfiguration } from "../../Common/store/slices/common";
import gearBoxImage from "../Assets/Sizing/additional_transmission_gear.png";
import mechanicsMiddle from "../Assets/Sizing/additional_transmission_shaft.png";
import mechanicsTail from "../Assets/Sizing/mechanism_motor.png";
import mechanicsHead from "../Assets/Sizing/mechanism_universal_833x542.png";
import { selectSizingGearBox, setCurrentSizingStep, setSizingGearBox } from "../store/slices/retrofit";
import CustomSelect from "./CustomSelect";
import TopSection from "./TopSection";

const gearBoxOptions = ["No additional gear", "Additional gearbox", "Belt drive", "Coupling"];
const GEAR_RATIO = "gearRatio";

const GearBoxOptions = ({ handleInputChange, gearBox }) => {
  const gearBoxVal = gearBox.isGearBox;
  return (
    <div className="ml-2 mb-2 mt-2 flex flex-col items-start space-y-3 bg-[#FFFFFF]">
      {gearBoxOptions.map((option) => {
        return (
          <div className="flex space-x-2 items-center">
            <input
              onClick={handleInputChange}
              checked={gearBoxVal === option}
              type="checkbox"
              value=""
              name={option}
              className={`w-5 h-5 appearance-none border-1 group-hover:border-2 group-hover:border-primary rounded-full cursor-pointer transition-all duration-100 checked:bg-primary focus:ring-primary ${"bg-white"}`}
            />
            <label className="text-sm" htmlFor={option}>
              {option}
            </label>
          </div>
        );
      })}
    </div>
  );
};

const LeftSection = ({ handleInputChange, gearBox }) => {
  return (
    <div className="w-[70%] flex-col">
      <div className="mb-4">Add description here to guide the user better throughout the process for each tab.</div>
      <div className="pl-3 flex flex-col space-y-3">
        <div className="flex mt-[10%]">
          <div className="flex-1 border-y-2 border-transparent">
            <img src={mechanicsHead} alt="linearAxis" className="w-full" />
          </div>
          <div className="flex-1 border-2 border-solid border-primary rounded-lg">
            {gearBox.isGearBox !== "No additional gear" && (
              <img src={gearBoxImage} alt="linearAxis" className="w-full bg-gray-100 rounded-t-lg" />
            )}
            {gearBox.isGearBox === "No additional gear" && (
              <img src={mechanicsMiddle} alt="linearAxis" className="w-full bg-gray-100 rounded-t-lg" />
            )}
            <GearBoxOptions handleInputChange={handleInputChange} gearBox={gearBox} />
          </div>
          <div className="flex-1 border-y-2 border-transparent pointer-events-none opacity-50">
            <img src={mechanicsMiddle} alt="linearAxis" className="w-full" />
          </div>
          <div className="flex-1 border-y-2 border-transparent pointer-events-none opacity-50">
            <img src={mechanicsTail} alt="linearAxis" className="w-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

const AddOption = ({
  name,
  label,
  subLabel,
  unitVal,
  value = "",
  handleInputChange,
  options,
  labelRequired = false,
  inputClasses = "rounded-md"
}) => {
  return (
    <>
      <div className="flex flex-col ml-3">
        <div className="flex-row">
          <span className="flex-1 md:text-xs">{label}</span>
          {labelRequired && <span className="light-error-active">*</span>}
        </div>
        <span className="flex-1 md:text-xs mr-5">{subLabel}</span>
        <div className="flex flex-row">
          <Input
            type="number"
            pattern="^\d+(\.\d{1,2})?$"
            autoComplete="off"
            required
            name={name}
            value={value}
            label=""
            placeHolder="Enter value"
            labelRequired={labelRequired}
            onChange={handleInputChange}
            inputClasses={inputClasses}
            inputRounded={true}
          />
          <CustomSelect name={name} unitVal={unitVal} handleInputChange={handleInputChange} options={options} />
        </div>
      </div>
    </>
  );
};

const CalculatorSection = () => {
  return (
    <div className="text-secondary text-xs cursor-pointer h-max flex justify-end">
      <MsxIcons name="3d_viewer" size="nano" className="mr-1 mb-4" />
      <span>Open calculator</span>
    </div>
  );
};

const ApplySection = ({ enable, handleContinue }) => {
  return (
    <div className="flex justify-center">
      <qds-button
        id="naviguator-button-continue"
        {...(!enable ? { disabled: "" } : {})}
        importance="emphasized"
        onClick={() => {
          handleContinue();
        }}
        text="Go to Sizing results"
      ></qds-button>
    </div>
  );
};

const RightSection = ({ gearBox, handleInputChange, handleContinue }) => {
  const [hideDetails, setHideDetails] = useState(false);
  const subLabeltext = "A ??? is any component, sensor/actuator, that is connected to the Control ";
  return (
    <div className="w-[30%] border-solid border-l-[1px] shadow-md">
      <div className=" flex flex-col h-full">
        <div className="mt-1 mb-1 h-[5%]">
          <TopSection hideDetails={hideDetails} setHideDetails={setHideDetails} />
        </div>
        <div className="flex border-solid border-t-[1px] mb-2" />
        {gearBox.isGearBox === "Additional gearbox" && (
          <div className="ml-3 thin-scrollbar overflow-visible h-full">
            <AddOption
              name={GEAR_RATIO}
              label="Gear ratio"
              options={["°.1-1", "mm.1-1", "in.1-1", "Unit.1-1"]}
              labelRequired={true}
              handleInputChange={handleInputChange}
              value={gearBox.gearRatio}
              unitVal={gearBox.gearRatioUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <AddOption
              name="momentOfInertia"
              label="Moment of Inertia"
              options={["kg.cm²", "kg.m²", "oz.in²", "lb.in²", "lb.ft²"]}
              handleInputChange={handleInputChange}
              value={gearBox.momentOfInertia}
              unitVal={gearBox.momentOfInertiaUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <CalculatorSection />
            <AddOption
              name="efficiency"
              label="Efficiency"
              options={["N.m", "lb.ft"]}
              handleInputChange={handleInputChange}
              value={gearBox.efficiency}
              unitVal={gearBox.efficiencyUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <AddOption
              name="kineticFrictionTorque"
              label="Kinetic friction torque"
              options={["N.m", "lb.ft"]}
              handleInputChange={handleInputChange}
              value={gearBox.kineticFrictionTorque}
              unitVal={gearBox.kineticFrictionTorqueUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <AddOption
              name="mViscous"
              label="M viscous"
              options={["N.m/(1000 1.s-1)", "N.m/(1000 1.min-1)", "lb.ft/(1000 1.s-1)", "lb.ft/(1000 1.min-1)"]}
              handleInputChange={handleInputChange}
              value={gearBox.mViscous}
              unitVal={gearBox.mViscousUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <div className="mb-3" />
          </div>
        )}
        {gearBox.isGearBox === "Belt drive" && (
          <div className="ml-3 thin-scrollbar overflow-visible h-full">
            <AddOption
              label="Diameter of the drive pulley"
              name="drivePulleyDiameter"
              options={["m", "mm", "cm", "in", "ft"]}
              handleInputChange={handleInputChange}
              value={gearBox.drivePulleyDiameter}
              unitVal={gearBox.drivePulleyDiameterUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <AddOption
              label="Moment of inertia of the drive pulley"
              name="drivePulleyMomentOfInertia"
              options={["kg.cm²", "kg.m²", "oz.in²", "lb.in²", "lb.ft²"]}
              handleInputChange={handleInputChange}
              value={gearBox.drivePulleyMomentOfInertia}
              unitVal={gearBox.drivePulleyMomentOfInertiaUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <CalculatorSection />
            <AddOption
              label="Diameter of the driven pulley"
              name="drivenPulleyDiameter"
              options={["m", "mm", "cm", "in", "ft"]}
              handleInputChange={handleInputChange}
              value={gearBox.drivenPulleyDiameter}
              unitVal={gearBox.drivenPulleyDiameterUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <AddOption
              label="Moment of inertia of the driven pulley"
              name="drivenPulleyMomentOfInertia"
              options={["kg.cm²", "kg.m²", "oz.in²", "lb.in²", "lb.ft²"]}
              handleInputChange={handleInputChange}
              value={gearBox.drivenPulleyMomentOfInertia}
              unitVal={gearBox.drivenPulleyMomentOfInertiaUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <CalculatorSection />
            <AddOption
              label="Mass of the belt"
              name="beltMass"
              options={["kg", "g", "oz", "lb"]}
              handleInputChange={handleInputChange}
              value={gearBox.beltMass}
              unitVal={gearBox.beltMassUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <CalculatorSection />
            <AddOption
              label="Additional moment of inertia"
              name="additionalMomentOfInertia"
              options={["kg.cm²", "kg.m²", "oz.in²", "lb.in²", "lb.ft²"]}
              handleInputChange={handleInputChange}
              value={gearBox.additionalMomentOfInertia}
              unitVal={gearBox.additionalMomentOfInertiaUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <CalculatorSection />
            <AddOption
              label="Efficiency"
              name="beltDriveEfficiency"
              options={["%", "n"]}
              handleInputChange={handleInputChange}
              value={gearBox.beltDriveEfficiency}
              unitVal={gearBox.beltDriveEfficiencyUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <AddOption
              label="Kinetic friction torque"
              name="beltDriveKineticFrictionTorque"
              options={["N.m", "lb.ft"]}
              handleInputChange={handleInputChange}
              value={gearBox.beltDriveKineticFrictionTorque}
              unitVal={gearBox.beltDriveKineticFrictionTorqueUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <AddOption
              label="M viscous"
              name="beltDriveMViscous"
              options={["N.m/(1000 1.s-1)", "N.m/(1000 1.min-1)", "lb.ft/(1000 1.s-1)", "lb.ft/(1000 1.min-1)"]}
              handleInputChange={handleInputChange}
              value={gearBox.beltDriveMViscous}
              unitVal={gearBox.beltDriveMViscousUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <div className="mb-3" />
          </div>
        )}
        {gearBox.isGearBox === "Coupling" && (
          <div className="ml-3 thin-scrollbar overflow-visible h-full">
            <AddOption
              label="Moment of inertia"
              name="couplingMomentOfInertia"
              options={["kg.cm²", "kg.m²", "oz.in²", "lb.in²", "lb.ft²"]}
              handleInputChange={handleInputChange}
              value={gearBox.couplingMomentOfInertia}
              unitVal={gearBox.couplingMomentOfInertiaUnit}
              subLabel={hideDetails ? subLabeltext : ""}
            />
            <CalculatorSection />
          </div>
        )}

        <div class="flex p-2 justify-center absolute bottom-0 bg-white border-t right-[18px] w-[30%] z-10">
          <ApplySection enable={gearBox.isValid} handleContinue={handleContinue} />
        </div>
      </div>
    </div>
  );
};

const SizingGearBoxSelection = () => {
  const gearBox = useSelector(selectSizingGearBox);
  const dispatch = useDispatch();

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    const isGearRatioValid = name === GEAR_RATIO && value === "" ? false : true;
    const isValid = name === GEAR_RATIO ? isGearRatioValid : gearBox.isValid;
    // const gearBoxVal = name === gearBoxOptions[1] ? true : false;
    const resolvedName = gearBoxOptions.includes(name) ? "isGearBox" : name;
    const newData = {
      ...gearBox,
      [resolvedName]: gearBoxOptions.includes(name) ? name : value,
      isValid: isValid
    };
    dispatch(setSizingGearBox(newData));
  };

  const handleContinue = () => {
    dispatch(setCurrentSizingStep(STEPS.RETROFIT_SIZING.SIZING_RESULTS));
    dispatch(updateConfiguration());
  };

  return (
    <div>
      <div className="flex">
        <LeftSection handleInputChange={handleInputChange} gearBox={gearBox} />
        {gearBox.isGearBox !== "No additional gear" && (
          <RightSection handleInputChange={handleInputChange} gearBox={gearBox} handleContinue={handleContinue} />
        )}
      </div>
      {gearBox.isGearBox === "No additional gear" && (
        <div className="flex p-2 justify-center space-x-5 absolute bottom-0 bg-white border-t left-0 w-full drop-shadow-xl hover:drop-shadow-2xl transition-all duration-75 z-10">
          <qds-button
            id="naviguator-button-continue"
            onClick={() => {
              handleContinue();
            }}
            text="Cancel and discard changes"
          />
          <qds-button
            id="naviguator-button-continue"
            importance="emphasized"
            onClick={() => {
              handleContinue();
            }}
            text="Proceed to Bundle Selection"
          />
        </div>
      )}
    </div>
  );
};

export default SizingGearBoxSelection;
